import { assertNever } from '@ofmos/shared/exhaustive';
import { parseWorkflowActorContext } from '@ofmos/shared/workflow';
import type { Actor, ActorSource } from '../../data/client';
import { Avatar, cx, Icon, type IconName } from '../../ui';

/**
 * WHO wrote something, and WHAT KIND of writer it was (INV-3).
 *
 * A CRM that lets an AI agent write has one hard requirement in its timeline: an
 * agent write must never be mistakable for a human one. A person therefore gets an
 * avatar with initials and a name and nothing else; every automated source gets an
 * icon, a named kind and - where it has one - the context it acted in. The single
 * accent colour is spent on the agent, because that is the row a human audits.
 */
interface SourcePresentation {
  readonly kind: string;
  readonly icon: IconName;
  readonly emphasised: boolean;
}

/** `null` means "a person": no kind label, because the avatar already says it. */
function presentationOf(source: ActorSource): SourcePresentation | null {
  switch (source) {
    case 'MANUAL':
      return null;
    case 'AGENT':
      return { kind: 'AI assistant', icon: 'agent', emphasised: true };
    case 'IMPORT':
      return { kind: 'Import', icon: 'arrow-down', emphasised: false };
    case 'WORKFLOW':
      return { kind: 'Workflow', icon: 'settings', emphasised: false };
    case 'API':
      return { kind: 'API', icon: 'link', emphasised: false };
    case 'SYSTEM':
      return { kind: 'System', icon: 'info', emphasised: false };
    default:
      return assertNever(source, 'ActorLabel.presentationOf');
  }
}

export interface ActorLabelProps {
  readonly actor: Actor;
}

export function ActorLabel({ actor }: ActorLabelProps) {
  const presentation = presentationOf(actor.source);
  const name = footerNameOf(actor, presentation);

  if (presentation === null) {
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        <Avatar name={name} size="sm" />
        <span className="truncate text-sm font-medium text-ink">{name}</span>
      </span>
    );
  }

  /**
   * THE KIND IS PRINTED ONLY WHERE IT ADDS SOMETHING THE NAME DOES NOT SAY (wave 3 round 2):
   * "Arif" plus "Import" is information, "an import" plus "Import" is one fact twice. A key's
   * kind word ("API") is not printed at all: the configured source name says who wrote, and
   * the word is platform vocabulary (DESIGN.md D16).
   */
  const kind =
    actor.source === 'API' || namesItsOwnKind(name, presentation.kind) ? null : presentation.kind;

  return (
    /*
      WAVE 3 (R-B1): A SYMBOL AND TWO WORDS, NOT A BOXED GLYPH AND A WORD IN CAPITALS.
      "IMPORT" in letter-spaced capitals inside a hairline square is the chip the owner read
      as a foreign component in the rebuilt stream (owner-25). Apple's own rows put the
      symbol in the leading slot at the label's colour and say the rest in ordinary words:
      the kit's Lists row masters carry a leading Symbol accessory and a secondary label, and
      neither is boxed. The single accent is still spent on the agent, which is the one row a
      human audits, and it is spent on the SYMBOL rather than on a frame around it.
      kit: `Apple iOS 27 UI Kit.sketch / Lists/Light/Rows/Accessories - Leading - Regular/With Symbol`.
    */
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <span
        className={cx(
          'inline-flex shrink-0 items-center justify-center',
          presentation.emphasised ? 'text-accent' : 'text-ink-muted',
        )}
      >
        <Icon name={presentation.icon} size="0.9rem" />
      </span>
      <span className="truncate text-sm font-medium text-ink">{name}</span>
      {kind === null ? null : <span className="text-2xs text-ink-muted">{kind}</span>}
      {/*
        NO CONTEXT, EVER (BR-RECORD; DESIGN.md Part 1 priority 4 and D16: "no system
        identifiers (file names, key labels, slugs) in the interface"). This line printed
        `via {actor.context}`, which for an importer is `import:<run id>:<file name>` and for a
        key is the key's own label ("Inbound source: <slug>") - the owner read the key label in
        the footer on 4 Oct. Who wrote is the name; the server resolves a key to the source it
        belongs to (`records/actor-source-names.ts`).
      */}
    </span>
  );
}

/**
 * THE WRITER'S NAME IN THE FOOTER (BR-RECORD): a person's display name, the configured name
 * of the source a key belongs to (the server puts it in `displayName`), or, for an import or
 * a key no source owns, "an import" - the product's own word for a run with no file name
 * (`csv/ImportsPage.tsx`). Never an id, a file name or a key label (D16).
 */
function footerNameOf(actor: Actor, presentation: SourcePresentation | null): string {
  if (actor.source === 'AGENT') return 'AI assistant';
  if (actor.displayName !== undefined) return actor.displayName;
  if (actor.source === 'IMPORT' || actor.source === 'API') return 'an import';
  return presentation?.kind ?? 'Unknown';
}

/**
 * Whether the writer's NAME already carries the kind, as a whole word: "CSV import" says
 * Import, "Systemhaus" does not say System. The word boundary is the whole reason this is a
 * regular expression rather than `includes`.
 */
function namesItsOwnKind(name: string, kind: string): boolean {
  const escaped = kind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(name);
}

/**
 * THE WRITER AS ONE PLAIN PHRASE, for the stream's single-line row (wave 4, C2-12).
 *
 * The component above is the right part where a writer has a line of its own - a note card's
 * author, the record's created-and-updated footnote - because there it can spend an avatar
 * and a symbol on saying WHAT KIND of writer this was. A stream entry is one list row now
 * (`spec/screens/record.md` section 2 item 5: title, secondary line, time), and its secondary
 * line is text, so the same fact has to be said in words.
 *
 * INV-3 IS WHY THE KIND IS STILL THERE: an agent write must never be mistakable for a human
 * one, and in a row with no avatar the only thing that can carry that is the wording. A person
 * is their name and nothing else; an automated writer names its kind unless its own name
 * already does ("CSV import" does not become "CSV import (Import)").
 * hig: https://developer.apple.com/design/human-interface-guidelines/writing
 */
export interface ActorParts {
  readonly name: string;
  /** `null` for a person, and for a writer whose own name already says its kind. */
  readonly kind: string | null;
  /** The file, the session or whatever else an automated writer acted in, when it reads. */
  readonly context: string | null;
}

/**
 * THE TWO ACTOR SHAPES THIS SCREEN HOLDS. An EVENT carries the client's display-augmented
 * actor; an ANNOTATION carries the shared one with its name resolved beside it
 * (`createdByName`). Both answer the three questions below, so the reading is one function
 * and a comment can never describe its writer differently from the event about it.
 */
export interface WritingActor {
  readonly source: ActorSource;
  readonly userId?: string | undefined;
  readonly context?: string | undefined;
  readonly displayName?: string | undefined;
}

export function actorParts(actor: WritingActor): ActorParts {
  const presentation = presentationOf(actor.source);
  const name =
    actor.source === 'AGENT'
      ? 'AI assistant'
      : (actor.displayName ?? actor.userId ?? presentation?.kind ?? 'Unknown');
  /*
    THE CONTEXT IS SHOWN ONLY WHEN IT SAYS SOMETHING (workflow-builder.md D24, N3): for an
    importer it is the file name. Agent session and key labels are internal; for a
    WORKFLOW it is 86 characters of uuid beside the workflow's own name.
  */
  const context =
    actor.source !== 'AGENT' &&
    actor.context !== undefined &&
    parseWorkflowActorContext(actor.context) === undefined
      ? `via ${actor.context}`
      : null;
  if (presentation === null) return { name, kind: null, context };
  return {
    name,
    kind: namesItsOwnKind(name, presentation.kind) ? null : presentation.kind,
    context,
  };
}

/** The same facts as one phrase, for a place that can only hold a string. */
export function actorSentence(actor: WritingActor): string {
  const parts = actorParts(actor);
  return parts.kind === null ? parts.name : `${parts.name} (${parts.kind})`;
}
